import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import RouteNames from "../../routes/type";
import { BackButton, Button } from "../../components";
import { classNames } from "../../helper";
import { PiCheckBold } from "react-icons/pi";

import { FormProvider, useForm } from "react-hook-form";
import { connect, useDispatch } from "react-redux";
import { isScopeLoading } from "../../store/scope-loading/helpers";
import { RootState } from "../../store/root-reducer";
import queryString from "query-string";
import Loader from "../../components/Loader";

import { useTranslation } from "react-i18next";
import { InProcessClaim } from "../../components/Modal";
import { getScopeError } from "../../store/scope-error/helpers";
import LinkNotValid from "../../components/LinkNotValid";
import { aceClaimStepsData } from "./types";
import SelectItems from "./SelectItem";
import ItemDetailes from "./ItemDetailes";
import ReportDamages from "./ReportDamages";
import ReviewClaim from "./ReviewClaim";
import SubmitClaim from "./SubmitClaim";
import { fetchAceOptions } from "../../store/user/actions";
import {
  fetchAceClaimInfo,
  insertClaimItemDamages,
  insertClaimItems,
  submitClaim,
} from "../../store/ace-claim/actions";
import { AceClaimScope } from "../../store/ace-claim/types";
import { nextStepRedirect } from "../../store/ace-claim/helpers";
import moment from "moment";
import { UserScope } from "../../store/user/types";

const lastStepNum = aceClaimStepsData.length;
const sucessStepNum = lastStepNum + 1;

function CreateAceClaim({ loading, error, warranty_items }) {
  let [searchParams] = useSearchParams();
  const formMethods = useForm({
    defaultValues: {
      selectedPlan: "",
      claimItems: {},
      claimItemDamage: [],
    },
  });
  const {
    handleSubmit,
    formState: { isValid },
    getValues,
  } = formMethods;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stepId: number = parseInt(searchParams.get("step") || "");
  const claimId: string = searchParams.get("claimId") || "";
  const warranty_id: string = searchParams.get("warranty_id") || "";
  const cust_id: string = searchParams.get("cust_id") || "";
  const [openClaimInProcessModal, setOpenClaimInProcessModal] = useState({
    isShow: false,
    claimStatusCode: "",
  });

  useEffect(() => {
    if (+stepId == 1) {
      navigate(`/${RouteNames.SELECT_PLAN}`, { replace: true });
    } else if (
      (!stepId && !/^[0-9]*$/.test(`${stepId}`)) ||
      stepId > sucessStepNum
    ) {
      nextStepRedirect(2);
    }
    return () => {};
  }, [navigate, stepId]);

  const modelNumberForOptions = useMemo(() => {
    if (warranty_items.length > 0) {
      return warranty_items[0].modelNumber;
    }
    return null;
  }, [warranty_items]);

  useEffect(() => {
    if (modelNumberForOptions) {
      dispatch(fetchAceOptions(modelNumberForOptions));
    }
    return () => {};
  }, [dispatch, modelNumberForOptions]);

  useEffect(() => {
    if (claimId && warranty_id && cust_id) {
      dispatch(
        fetchAceClaimInfo({
          claimId,
          warranty_id,
          cust_id,
        })
      );
    }
  }, [claimId, dispatch, warranty_id, cust_id, stepId]);

  const currentStep = aceClaimStepsData.find(
    (item) => item.step_num === stepId
  );

  const navigateToNextStep = (step) => {
    const parsed = queryString.parse(window.location.search);
    navigate(
      `/${RouteNames.CREATE_ACE_CLAIM}?${queryString.stringify({
        ...parsed,
        step: step ?? stepId + 1,
      })}`
    );
  };

  const handleNext = () => {
    const values = getValues();
    switch (stepId) {
      case 2:
        navigateToNextStep(3);
        break;

      case 3:
        let mappedData = [];
        for (const key in values.claimItems) {
          const element = values.claimItems[key];
          const currentClaimItems =
            warranty_items?.find((item) => item.trackKey === key) || {};
          mappedData.push({
            warrentyId: +warranty_id,
            claimId: +claimId,
            selectedItemId: +currentClaimItems.id,
            material: element.material,
            color: element.color,
          });
        }
        dispatch(insertClaimItems(mappedData));
        break;

      case 4:
        const mappedDamagesData = [];
        for (const key in values.claimItemDamage) {
          const element = key.includes("undefined")
            ? []
            : values.claimItemDamage[key] || [];
          element.forEach((item) =>
            item.isSavedInDB
              ? null
              : mappedDamagesData.push({
                  selectedItemId: String(key),
                  claimId: +claimId,
                  damageType: item?.damage_type,
                  specificLocation: item?.damage_area,
                  damageDate: item?.when_did_you_notice_the_damage
                    ? moment(item?.when_did_you_notice_the_damage).format(
                        "YYYY-MM-DD"
                      )
                    : null,
                  howDidOccur: item?.how_did_it_happen,
                  actionTaken: item?.did_you_take_any_steps_to_remedy,
                  notes: item?.note,
                })
          );
        }
        dispatch(insertClaimItemDamages(mappedDamagesData));
        break;

      case 5:
        dispatch(submitClaim());
        break;

      default:
        break;
    }
  };

  const stepComponents = {
    2: <SelectItems key="stepId2" />,
    3: <ItemDetailes key="stepId3" />,
    4: <ReportDamages key="stepId4" />,
    5: <ReviewClaim key="stepId5" />,
  };

  if (error === "CLAIM_NOT_FOUND") {
    return <LinkNotValid />;
  }

  if (stepId === sucessStepNum) {
    return (
      <Loader isLoading={loading}>
        <SubmitClaim />
      </Loader>
    );
  }

  return (
    <div className="px-4 space-y-3">
      <div className="mt-4 flex justify-between items-center">
        <BackButton
          onClick={() => {
            if (stepId === 1) {
              navigate(`/${RouteNames.SELECT_SEARCH_METHOD}`);
            } else {
              navigateToNextStep(stepId - 1);
            }
          }}
        />
        <div className="flex flex-row gap-2">
          {aceClaimStepsData.map(({ step_num: step }) => {
            const isCompleted = step < stepId;
            const isDraft = step > stepId;
            return (
              <button
                disabled
                key={step}
                className={classNames(
                  "size-7 flex rounded-full justify-center items-center text-base",
                  isDraft
                    ? "bg-[#009CAA] bg-opacity-15 text-[#009CAA] "
                    : "bg-[#009CAA] text-white"
                )}
              >
                {isCompleted ? <PiCheckBold size={16} color="white" /> : step}
              </button>
            );
          })}
        </div>
      </div>
      <h3 className="text-2xl font-semibold">{t(currentStep?.title)}</h3>
      <form onSubmit={handleSubmit(handleNext)}>
        <Loader isLoading={loading} />
        <FormProvider {...formMethods}>
          {stepComponents[stepId] || null}
        </FormProvider>
        <Button
          className="my-4"
          submit
          isLoading={loading}
          disabled={!(stepId === 4 || isValid)}
        >
          {stepId === lastStepNum ? t("Submit") : t("Next")}
        </Button>
      </form>
      <InProcessClaim
        isOpen={openClaimInProcessModal.isShow}
        claimStatusCode={openClaimInProcessModal.claimStatusCode}
        onClose={() =>
          setOpenClaimInProcessModal({
            isShow: false,
            claimStatusCode: null,
          })
        }
      />
    </div>
  );
}
const mapStateToProps = (state: RootState) => ({
  loading:
    isScopeLoading(state.scopeLoading, AceClaimScope) ||
    isScopeLoading(state.scopeLoading, UserScope),
  warranty_items: state?.aceClaim?.warranty_items || [],
  error: getScopeError(state.scopeError, AceClaimScope),
  plan: state.aceClaim.plan,
});

export default connect(mapStateToProps)(CreateAceClaim);
